'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

import HouseAd from './HouseAd';
import { useAdFallback } from './useAdFallback';

const ADSENSE_CLIENT_ID = 'ca-pub-2214184889025947';

/**
 * Native multiplex ("autorelaxed") ad — a grid of "related content"-style units
 * for the bottom of a page, below the article/tool and its related links. Reads
 * as a recommendations block rather than a banner, so it's non-intrusive at the
 * end of the page. Requires the adsbygoogle.js loader on the page.
 */
/**
 * DISABLED 2026-07-28 while testing whether manual inventory is suppressing
 * Auto Ads revenue.
 *
 * Earnings peaked 2026-07-17 and fell ~74% on 07-18/19, holding down for nine
 * days, while traffic over the same window ROSE 12% (avg 5,778 -> 6,455
 * pageviews/day). So it is not a traffic story. The drop lands exactly on the
 * commits that added this and the other manual units (12bce607, f72d7ea7,
 * 78e12cb3).
 *
 * The mechanism fits the per-unit report: Auto Ads ran $4.16 impression RPM at
 * 90.67% viewability while manual ran $0.86 at 39.60%, and manual carried 3x
 * the impressions for less money. Auto Ads places around existing units, so
 * flooding a page with manual slots leaves it fewer good positions and its
 * high-value placements get displaced by ones earning a fifth as much. Multiplex
 * is the worst offender in that report — the largest impression share of any
 * unit at 12-24% viewability and $0.53-1.11 RPM.
 *
 * The position still carries a house ad rather than going blank. What competes
 * with Auto Ads is the <ins class="adsbygoogle"> element — Google detects
 * existing ad units and avoids placing near them. A house ad is our own markup
 * plus an inline SVG, with no <ins> and no ad iframe, so Auto Ads should not
 * read it as an ad. Removing the AdSense unit therefore still isolates the
 * variable under test, and the slot earns funnel exposure instead of nothing.
 *
 * Flip to `true` to restore. If site earnings do not recover within a week or
 * so, this was not the cause and it should go back.
 */
const MULTIPLEX_ENABLED = false;

export default function MultiplexAd({ slot }: { slot: string }) {
  const pushed = useRef(false);
  const insRef = useRef<HTMLModElement | null>(null);
  const pathname = usePathname();
  const { unfilled, blocked } = useAdFallback(insRef, { slot: slot, placement: 'multiplex' });

  useEffect(() => {
    if (!MULTIPLEX_ENABLED || pushed.current) return;
    pushed.current = true;
    try {
      const w = window as unknown as { adsbygoogle: unknown[] };
      (w.adsbygoogle = w.adsbygoogle || []).push({});
    } catch {
      // Loader not ready yet or slot already filled — safe to ignore.
    }
  }, []);

  // After the hooks, so hook order is unchanged when the flag flips. The
  // AdSense unit is gone but the position keeps our own inventory.
  if (!MULTIPLEX_ENABLED) {
    return (
      <section className="py-8 px-4">
        <div className="mx-auto max-w-4xl"><HouseAd pathname={pathname} blocked={blocked} /></div>
      </section>
    );
  }

  return (
    <section className="py-8 px-4">
      <div className="mx-auto max-w-4xl">
        {!unfilled && (
          <div className="mb-1 select-none text-center text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
            Advertisement
          </div>
        )}
        {/* Unmount the <ins> once it is known unfilled rather than just hiding
            it. Ad blockers apply cosmetic filters as well as network blocks,
            and a :has(> ins.adsbygoogle) rule would hide this whole container —
            taking the house ad with it. Removing the element means such a rule
            has nothing to match. Detection has already completed by this point
            (useAdFallback settles once), so the ref is no longer needed. */}
        {!unfilled && (
          <ins
            ref={insRef}
            className="adsbygoogle"
            style={{ display: 'block' }}
            data-ad-format="autorelaxed"
            data-ad-client={ADSENSE_CLIENT_ID}
            data-ad-slot={slot}
          />
        )}
        {unfilled && <HouseAd pathname={pathname} blocked={blocked} />}
      </div>
    </section>
  );
}
